import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  onSwitch: () => void;
}

export const Login: React.FC<Props> = ({ onSwitch }) => {
  const { login } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.error || t('loginInvalidCreds'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 relative px-4 overflow-hidden font-sans">
      {/* Decorative Glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl -z-10 pointer-events-none"></div>

      {/* Language Switcher Top Right */}
      <div className="absolute top-6 right-6 flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200/80 shadow-xs">
        <button
          onClick={() => setLanguage('en')}
          className={`px-2.5 py-1 rounded-lg text-xs font-black transition-all ${
            language === 'en' 
              ? 'bg-slate-900 text-white shadow-xs' 
              : 'text-slate-600 hover:text-slate-950'
          }`}
        >
          EN
        </button>
        <button
          onClick={() => setLanguage('sk')}
          className={`px-2.5 py-1 rounded-lg text-xs font-black transition-all ${
            language === 'sk' 
              ? 'bg-slate-900 text-white shadow-xs' 
              : 'text-slate-600 hover:text-slate-950'
          }`}
        >
          SK
        </button>
      </div>

      <div className="max-w-md w-full bg-white rounded-3xl p-8 sm:p-10 border border-slate-200/80 shadow-xl relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-2 mb-3">
            <img 
              src="/assets/anogi_logo.png" 
              alt="Anogi Enterprises Limited" 
              className="h-24 w-auto object-contain drop-shadow-sm hover:scale-105 transition-transform"
            />
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Anogi Traders Desk</h2>
          <p className="text-slate-500 mt-1 text-xs font-medium">
            {t('loginSubtitle')}
          </p>
        </div>

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3.5 text-xs font-bold mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1.5">
              {t('loginEmail')}
            </label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-xs font-bold focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 transition-all"
              placeholder={language === 'sk' ? 'email alebo prihlasovacie meno' : 'email or username'}
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-1.5">
              {t('loginPassword')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-xs font-bold focus:outline-none focus:bg-white focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 transition-all"
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs py-3.5 rounded-xl transition-all shadow-sm active:scale-[0.99] disabled:opacity-50 mt-2"
          >
            {loading ? t('loginSubmitting') : t('loginSubmit')}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-slate-500 font-medium">
          {t('loginNewUser')}{' '}
          <button 
            type="button"
            onClick={onSwitch} 
            className="text-indigo-600 hover:text-indigo-800 font-extrabold transition-all underline underline-offset-4 ml-1"
          >
            {t('loginCreateAccount')}
          </button>
        </div>
      </div>
    </div>
  );
};

